/**
 * EntranceAnomaliesPage — 퇴실 미기록 확인 (구 '출입 이상 관리')
 *
 * ⚠️ 이 화면이 무엇인지 (2026-08-01 전면 개편)
 *   예전엔 중복 입실을 '악용 의심'으로 분류해 학생을 의심하는 화면이었다.
 *   그런데 실제 기록은 대부분 **퇴실을 안 찍고 나간 것**이고,
 *   매장 정책상 **퇴실이 없으면 그 세션은 순공에서 뺀다**(사용자 확정 정책, 버그 아님).
 *   → 그래서 이 목록의 진짜 의미는 "이 학생의 이 날은 공부 시간이 안 잡혔다"이고,
 *     화면의 역할은 **원장이 실제 퇴실 시각을 넣어 바로잡는 것**이다.
 *
 * 데이터: GET  /v1/owner/entrance-exit/anomalies
 *        POST /v1/owner/entrance-exit/anomalies/{logId}/resolve  { exit_time }
 *        POST /v1/owner/entrance-exit/anomalies/{logId}/keep
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../../../infrastructure/api';

interface RepeatStudent {
  user_id: string;
  user_name: string;
  count: number;
  same_day_count: number;
}

interface AnomalyItem {
  entry_confidence: number | null;   // 퇴실 안 찍은 그 입실의 얼굴인식 유사도
  next_confidence: number | null;    // 다시 찍은 입실의 유사도
  entry_method: string | null;
  next_method: string | null;
  low_confidence: boolean;           // 인식이 약함 → 오인식일 수 있음
  suspect: boolean;                  // 확실히 본인인데 운영 중 재입실 → 찍고 안 들어온 것 의심
  pattern: 'rescan' | 'same_day' | 'long';
  repeat_count: number;
  is_repeat: boolean;
  log_id: number;
  user_id: string;
  user_name: string;
  entry_at: string; // 퇴실을 안 찍은 그 입실
  next_entry_at: string; // 다음에 다시 찍은 입실
  gap_seconds: number;
  missed_date: string; // 순공이 안 잡힌 날
  study_minutes: number | null;
  resolution: string | null; // null | 'fixed' | 'kept'
  resolved_at: string | null;
}

interface AnomalyData {
  start_date: string;
  end_date: string;
  total: number;
  pending_count: number;
  same_day_count: number;
  low_confidence_count: number;
  suspect_count: number;
  repeat_threshold: number;
  repeat_students: RepeatStudent[];
  items: AnomalyItem[];
}

const fmtDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(
      d.getMinutes(),
    ).padStart(2, '0')}`;
  } catch {
    return iso;
  }
};

const fmtGap = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}분`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 ${m % 60 > 0 ? `${m % 60}분` : ''}`.trim();
  return `${Math.floor(h / 24)}일 ${h % 24 > 0 ? `${h % 24}시간` : ''}`.trim();
};

const todayStr = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
const daysAgoStr = (days: number): string =>
  new Date(Date.now() - days * 86400000).toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });

const EntranceAnomaliesPage: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(daysAgoStr(13));
  const [endDate, setEndDate] = useState<string>(todayStr());
  const [data, setData] = useState<AnomalyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [exitTime, setExitTime] = useState<string>('');
  const [saving, setSaving] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/v1/owner/entrance-exit/anomalies', {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data?.data ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(
    () =>
      (data?.items ?? [])
        .filter((i) => i.pattern !== 'rescan') // 게이트 재시도 노이즈는 숨김
        .filter((i) => (showResolved ? true : !i.resolution)),
    [data, showResolved],
  );

  const submitFix = async (item: AnomalyItem) => {
    if (!/^\d{1,2}:\d{2}$/.test(exitTime)) {
      alert('퇴실 시각을 HH:MM 형식으로 입력해 주세요. (예: 18:30)');
      return;
    }
    try {
      setSaving(item.log_id);
      const res = await apiClient.post(
        `/v1/owner/entrance-exit/anomalies/${item.log_id}/resolve`,
        { exit_time: exitTime },
      );
      alert(res.data?.message || '보정했습니다.');
      setEditing(null);
      setExitTime('');
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '보정에 실패했습니다.');
    } finally {
      setSaving(null);
    }
  };

  const keepAsIs = async (item: AnomalyItem) => {
    if (
      !window.confirm(
        `${item.user_name} 학생의 ${item.missed_date} 기록을 그대로 두시겠습니까?\n\n· 순공 시간은 지금 상태로 유지됩니다.\n· 목록에서만 내려갑니다.`,
      )
    )
      return;
    try {
      setSaving(item.log_id);
      await apiClient.post(`/v1/owner/entrance-exit/anomalies/${item.log_id}/keep`);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '처리에 실패했습니다.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">퇴실 미기록 확인</h1>
        <p className="text-sm text-gray-500 mt-1">
          퇴실을 찍지 않고 다시 입실한 기록입니다.{' '}
          <strong>퇴실이 없으면 그 시간은 순공에서 빠집니다.</strong>
          <br />
          실제 퇴실 시각을 넣으면 그날 순공이 다시 계산됩니다.
          <br />
          얼굴인식 유사도를 함께 표시합니다 — <strong>낮으면 오인식</strong>,{' '}
          <strong>높은데 운영 중 재입실이면 찍고 안 들어왔을</strong> 가능성이 있습니다.
        </p>
      </div>

      {/* 기간 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="date"
          value={startDate}
          max={endDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        />
        <span className="text-gray-400">~</span>
        <input
          type="date"
          value={endDate}
          min={startDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? '조회 중…' : '조회'}
        </button>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 ml-auto cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            style={{ width: '1rem', height: '1rem' }}
            className="accent-blue-600"
          />
          처리한 것도 보기
        </label>
      </div>

      {data && (
        <div className="flex gap-3 mb-4">
          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-amber-700">{data.pending_count}</div>
            <div className="text-xs text-amber-700">확인 필요</div>
          </div>
          <div className="flex-1 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-red-700">{data.suspect_count ?? 0}</div>
            <div className="text-xs text-red-700">찍고 안 들어옴 의심</div>
          </div>
          <div className="flex-1 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-purple-700">{data.low_confidence_count ?? 0}</div>
            <div className="text-xs text-purple-700">얼굴인식 확인</div>
          </div>
          <div className="flex-1 bg-gray-50 border rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-gray-700">{data.total}</div>
            <div className="text-xs text-gray-500">전체 (기간 내)</div>
          </div>
        </div>
      )}

      {/* ⭐ 반복되는 학생 — 한두 번 깜빡한 것과 구분해서 위에 따로 모은다.
             '입실 후 퇴실 없이 또 입실'이 반복되면 확인이 필요하다(사용자 지적). */}
      {data && (data.repeat_students?.length ?? 0) > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
            <span className="font-semibold text-red-800">반복되는 학생</span>
            <span className="text-xs text-red-600">
              기간 내 {data.repeat_threshold}회 이상 — 확인이 필요합니다
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.repeat_students.map((r) => (
              <span key={r.user_id}
                className="bg-white border border-red-200 rounded-lg px-3 py-1.5 text-sm">
                <span className="font-semibold text-gray-900">{r.user_name}</span>
                <span className="text-red-600 ml-1.5 font-medium">{r.count}회</span>
                {r.same_day_count > 0 && (
                  <span className="text-gray-500 text-xs ml-1">
                    (운영 중 {r.same_day_count}회)
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">불러오는 중…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <CheckCircleIcon className="w-12 h-12 mx-auto text-green-400 mb-2" />
          {showResolved ? '이 기간에 기록이 없습니다' : '확인이 필요한 기록이 없습니다'}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const noStudy = item.study_minutes === 0 || item.study_minutes === null;
            return (
              <div
                key={item.log_id}
                className={`rounded-xl border p-4 ${
                  item.resolution ? 'bg-gray-50 border-gray-200' : 'bg-white border-amber-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{item.user_name}</span>
                      <span className="text-xs text-gray-500">{item.missed_date}</span>
                      {item.resolution === 'fixed' && (
                        <span className="text-[11px] bg-green-100 text-green-700 rounded-full px-2 py-0.5">
                          보정 완료
                        </span>
                      )}
                      {item.resolution === 'kept' && (
                        <span className="text-[11px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">
                          확인함
                        </span>
                      )}
                      {!item.resolution && item.pattern === 'same_day' && !item.suspect && (
                        <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                          운영 중 재입실
                        </span>
                      )}
                      {!item.resolution && item.suspect && (
                        <span className="text-[11px] bg-red-600 text-white rounded-full px-2 py-0.5">
                          찍고 안 들어옴 의심
                        </span>
                      )}
                      {!item.resolution && item.low_confidence && (
                        <span className="text-[11px] bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">
                          얼굴인식 확인
                        </span>
                      )}
                      {!item.resolution && item.is_repeat && (
                        <span className="text-[11px] bg-red-600 text-white rounded-full px-2 py-0.5">
                          반복 {item.repeat_count}회
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-700 mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <ClockIcon className="w-4 h-4 text-gray-400" />
                      {fmtDateTime(item.entry_at)} 입실 → 퇴실 기록 없음
                      <span className="text-gray-400">
                        (다음 입실 {fmtDateTime(item.next_entry_at)} · {fmtGap(item.gap_seconds)} 뒤)
                      </span>
                    </div>

                    {(item.entry_confidence !== null || item.next_confidence !== null) && (
                      <div className="text-xs text-gray-500 mt-1">
                        얼굴인식 유사도 —{' '}
                        <span className={item.entry_confidence !== null && item.entry_confidence < 0.55 ? 'text-purple-700 font-semibold' : ''}>
                          입실 {item.entry_confidence ?? '-'}
                        </span>
                        {' · '}
                        <span className={item.next_confidence !== null && item.next_confidence < 0.55 ? 'text-purple-700 font-semibold' : ''}>
                          재입실 {item.next_confidence ?? '-'}
                        </span>
                        {item.low_confidence && (
                          <span className="text-purple-700"> (기준 0.50에 가까움 — 다른 사람일 수 있어요)</span>
                        )}
                        {item.suspect && (
                          <span className="text-red-600"> (본인이 확실 — 찍고 안 들어왔을 수 있어요)</span>
                        )}
                      </div>
                    )}

                    {!item.resolution && (
                      <div
                        className={`text-sm mt-1 flex items-center gap-1.5 ${
                          noStudy ? 'text-red-600' : 'text-gray-500'
                        }`}
                      >
                        {noStudy && <ExclamationTriangleIcon className="w-4 h-4" />}
                        {noStudy
                          ? `${item.missed_date} 순공 시간이 잡히지 않았습니다`
                          : `${item.missed_date} 순공 ${item.study_minutes}분으로 기록됨`}
                      </div>
                    )}
                  </div>

                  {!item.resolution && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {editing === item.log_id ? (
                        <>
                          <input
                            type="text"
                            value={exitTime}
                            onChange={(e) => setExitTime(e.target.value)}
                            placeholder="18:30"
                            className="w-24 border rounded-lg px-2 py-1.5 text-sm text-center"
                          />
                          <button
                            onClick={() => submitFix(item)}
                            disabled={saving === item.log_id}
                            className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium disabled:opacity-50"
                          >
                            {saving === item.log_id ? '저장 중…' : '저장'}
                          </button>
                          <button
                            onClick={() => {
                              setEditing(null);
                              setExitTime('');
                            }}
                            className="px-2 py-1.5 rounded bg-gray-100 text-gray-600 text-xs"
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditing(item.log_id);
                              setExitTime('');
                            }}
                            className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium whitespace-nowrap"
                          >
                            퇴실 시각 입력
                          </button>
                          <button
                            onClick={() => keepAsIs(item)}
                            disabled={saving === item.log_id}
                            className="px-3 py-1.5 rounded bg-gray-100 text-gray-600 text-xs whitespace-nowrap disabled:opacity-50"
                          >
                            그대로 두기
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {editing === item.log_id && (
                  <p className="text-xs text-gray-500 mt-2">
                    {fmtDateTime(item.entry_at)} ~ {fmtDateTime(item.next_entry_at)} 사이 시각을
                    입력해 주세요. 저장하면 퇴실 기록이 추가되고 그날 순공 시간이 다시 계산됩니다.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EntranceAnomaliesPage;
